import moment from 'moment-business-days'
import { ABSENCE_REASONS } from 'const/absenceReasons'
import { Absence, AbsenceReason } from 'Types/Absences/Absence'
import { Filter } from 'Types/Filter'
import { IHoliday } from 'Types/Holidays/IHoliday'

export const DATE_FORMAT = 'DD.MM.YYYY.'
const API_DATE_FORMAT = 'YYYY-MM-DD'

// Absence reasons that mean the user is not working that day (and doesn't need to track time)
export const TIME_OFF_REASONS: Partial<Record<AbsenceReason, { label: string; color: string }>> = {
  [AbsenceReason.VACATION]: ABSENCE_REASONS[AbsenceReason.VACATION],
  [AbsenceReason.SICK_LEAVE]: ABSENCE_REASONS[AbsenceReason.SICK_LEAVE],
  [AbsenceReason.PAID_LEAVE]: ABSENCE_REASONS[AbsenceReason.PAID_LEAVE],
}

export const isTimeOff = (absence: Absence) =>
  !!absence.absenceReason && !!TIME_OFF_REASONS[absence.absenceReason]

export const getMonthRange = (filters: Filter[]) => {
  const referenceDate = filters.find((filter) => filter.key === 'startDate')?.value

  return {
    monthStart: moment(referenceDate).startOf('month').format(API_DATE_FORMAT),
    monthEnd: moment(referenceDate).endOf('month').format(API_DATE_FORMAT),
  }
}

export const toHolidayDates = (holidays: IHoliday[] | undefined) =>
  new Set((holidays ?? []).map((holiday) => moment(holiday.date).format(DATE_FORMAT)))

// Part of the absence that falls inside the displayed month
export const clipToMonth = (absence: Absence, monthStart: string, monthEnd: string) => ({
  from: moment.max(moment(absence.absenceFrom), moment(monthStart)),
  to: moment.min(moment(absence.absenceTo), moment(monthEnd)),
})

export const countWorkingDays = (
  from: moment.Moment,
  to: moment.Moment,
  holidayDates: Set<string>
) => {
  let count = 0
  for (let day = from.clone(); day.isSameOrBefore(to, 'day'); day.add(1, 'day')) {
    if (day.isBusinessDay() && !holidayDates.has(day.format(DATE_FORMAT))) {
      count++
    }
  }
  return count
}

// Every day of the displayed month the user is on time off, formatted as DATE_FORMAT
export const expandAbsenceDates = (absences: Absence[], monthStart: string, monthEnd: string) => {
  const dates = new Set<string>()

  absences.filter(isTimeOff).forEach((absence) => {
    const { from, to } = clipToMonth(absence, monthStart, monthEnd)
    for (let day = from.clone(); day.isSameOrBefore(to, 'day'); day.add(1, 'day')) {
      dates.add(day.format(DATE_FORMAT))
    }
  })

  return dates
}
